import type {
  ClientConfirmReservationMutation,
  PackageDetailFragmentFragment,
  SignInProviderEnum,
} from "@/__generated__/graphql";
import { logAnalyticEvent, setAnalyticUserId } from "../../firebase";
import { trackHotjarEvent } from "../hotjar";
import { PosthogAnalytics } from "../posthog";
import { rudderanalytics } from "@/lib/rudderstack";
import { getAdsProporties, getGALinkerParam } from "@/lib/identifiers";
import type {
  AirconServiceName,
  BetaServiceName,
  ServiceName,
} from "@/types/service";
import { isLocal } from "@/lib/utils";

export type AnalyticsConversion =
  | EventSharerReferralClick
  | EventPageView
  | EventPurchase
  | EventAddToCart
  | EventFindAvailability
  | EventUserSignUp
  | EventUserSignIn
  | EventViewService
  | EventViewBetaService
  | EventWAClick
  | EventAppDownloadButton
  | EventRebookClick
  | EventRebookComplete
  | EventViewRecommendedService;

type Item = {
  item_id: string;
  item_category: string;
  price: number;
  quantity: number;
};

type EventRebookClick = {
  name: "rebook_click";
  values?: Record<string, string>;
};

type EventRebookComplete = {
  name: "rebook_complete";
  values: {
    currency: "SGD";
    value: number;
    transaction_id: string;
    coupon?: string;
    tax?: number;
    items: Item[];
  };
};

type EventPageView = {
  name: "page_view";
  values?: Record<string, string>;
};

type EventWAClick = {
  name: "wa_sales_button_click";
  values?: Record<string, string>;
};

type EventSharerReferralClick = {
  name: "sharer_referral_click";
  values?: {
    clientId: string;
    clientName: string;
  };
};

type EventPurchase = {
  name: "purchase";
  values: {
    currency: "SGD";
    value: number;
    transaction_id: string;
    coupon?: string;
    tax?: number; // we can put GST for this
    items: Item[];
  };
};

type EventAddToCart = {
  name: "add_to_cart";
  values: {
    currency: "SGD";
    value: number;
    items: Item[];
  };
};

type EventFindAvailability = {
  name: "find_availability";
  values: {
    currency: "SGD";
    value: number;
    items: Item[];
  };
};

type EventUserSignUp = {
  name: "user_sign_up";
  values: {
    method: SignInProviderEnum;
  };
};

type EventUserSignIn = {
  name: "user_sign_in";
  values: {
    method: SignInProviderEnum;
  };
};

type EventViewService = {
  name: "view_service";
  values: {
    // service_type: PackageDepartmentEnum;
    service_type: ServiceName | AirconServiceName;
  };
};
type EventViewBetaService = {
  name: "view_beta_service";
  values: {
    // service_type: PackageDepartmentEnum;
    service_type: BetaServiceName;
  };
};
type EventViewRecommendedService = {
  name: "view_recommended_service";
  values: {
    service_type: ServiceName | BetaServiceName;
  };
};

type EventAppDownloadButton = {
  name: "app_download_button_click";
  values?: Record<string, string>;
};

export async function sendAnalyticData(
  eventData: AnalyticsConversion,
): Promise<void> {
  if (isLocal) return;

  const properties = await getAdsProporties();

  trackHotjarEvent(eventData.name);
  PosthogAnalytics.track(eventData.name, eventData.values);
  logAnalyticEvent(eventData.name, {
    ...eventData.values,
    timestamp: Date.now(),
  });

  rudderanalytics.track(eventData.name, {
    ...eventData.values,
    ...properties,
  });
}

export function setAnalyticUser({
  userId,
  email,
  firstName,
}: {
  userId: string;
  email: string;
  firstName: string;
}) {
  if (isLocal) return;

  setAnalyticUserId(userId);
  PosthogAnalytics.setUserProperties(userId, email, firstName);
  rudderanalytics.identify(userId, {
    email,
    name: firstName,
  });
}

export function resetAnalyticUser() {
  if (isLocal) return;

  setAnalyticUserId(null);
  PosthogAnalytics.reset();
  rudderanalytics.reset();
}

export function transformBookingDataToAddToCart(
  packageDetails: PackageDetailFragmentFragment[],
): EventAddToCart["values"] {
  const totalServiceBillingValue = packageDetails.reduce<number>(
    (total, { serviceBillingValue }) => total + serviceBillingValue,
    0,
  );

  return {
    currency: "SGD",
    value: totalServiceBillingValue * (packageDetails[0].repeatEveryTimes || 1),
    items: packageDetails.map((packageDetail) => ({
      item_id: packageDetail.id,
      item_category: packageDetail.department,
      price: packageDetail.unitValue,
      quantity: packageDetail.units * (packageDetail.repeatEveryTimes || 1),
    })),
  };
}

export function transformReservationDataToPurchase(
  multiplier: number,
  reservation: NonNullable<
    ClientConfirmReservationMutation["clientConfirmReservation"]
  >,
): EventPurchase["values"] {
  // items price * quantity should equal to value;
  return {
    transaction_id: reservation.package.id,
    value: reservation.package.serviceBillingValue * multiplier,
    currency: "SGD",
    items: Array(multiplier).fill({
      item_id: reservation.package.packageDetail.id,
      item_category: reservation.package.packageDetail.serviceType,
      price: reservation.package.serviceBillingValue,
      quantity: 1,
    }) as Item[],
  };
}

export async function buildLinkWithTrackingUrl(url: string) {
  const { distinct_id, session_id } = PosthogAnalytics.getIdentifiers();
  const anonymousId = await rudderanalytics.getAnonymousId();
  const gaLinker = await getGALinkerParam();

  const urlObject = new URL(url);

  if (distinct_id) urlObject.searchParams.set("distinct_id", distinct_id);
  if (session_id) urlObject.searchParams.set("session_id", session_id);
  if (anonymousId) urlObject.searchParams.set("ajs_aid", anonymousId);
  if (gaLinker) urlObject.searchParams.set("_gl", gaLinker);

  return urlObject.toString();
}
